import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { useTranslation } from "../../hooks/useTranslation";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

interface InjuryStepProps {
  treatedBy: string;
  onChange: (field: string, value: string) => void;
}

const InjuryStep: React.FC<InjuryStepProps> = ({ treatedBy, onChange }) => {
  const { t } = useTranslation();

  const options = [
    {
      value: "YES",
      label: t("form.firstQuestion.option1"),
      icon: LocalHospitalIcon,
      color: {
        main: "#ef4444",
        light: "#fef2f2",
        border: "#fecaca",
        hover: "#fee2e2",
      },
    },
    {
      value: "NO",
      label: t("form.firstQuestion.option2"),
      icon: CheckCircleIcon,
      color: {
        main: "#22c55e",
        light: "#f0fdf4",
        border: "#bbf7d0",
        hover: "#dcfce7",
      },
    },
  ];

  return (
    <Box>
      <Typography
        variant="subtitle2"
        align="center"
        sx={{
          color: "#9ca3af",
          fontWeight: 500,
          mb: 2,
          fontSize: "0.875rem",
        }}
      >
        Question 1 out of 5
      </Typography>

      <Typography
        variant="h4"
        align="center"
        sx={{
          mb: 4,
          fontWeight: 700,
          color: "#1e293b",
          fontSize: { xs: "1.2rem", md: "1.5rem" },
        }}
      >
        1. {t("form.firstQuestion.question")}
        <span style={{ color: "#ef4444" }}>*</span>
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {options.map((option) => {
          const isSelected = treatedBy === option.value;
          const Icon = option.icon;

          return (
            <Button
              key={option.value}
              onClick={() => {
                onChange("treatedBy", option.value);
                onChange("physicalInjury", option.value);
              }}
              sx={{
                padding: "1.5rem",
                borderRadius: "12px",
                border: `2px solid ${
                  isSelected ? option.color.main : option.color.border
                }`,
                backgroundColor: isSelected
                  ? option.color.light
                  : "transparent",
                display: "flex",
                alignItems: "center",
                gap: 2,
                justifyContent: "flex-start",
                textTransform: "none",
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: option.color.hover,
                  borderColor: option.color.main,
                },
              }}
            >
              <Icon
                sx={{
                  fontSize: 32,
                  color: option.color.main,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: { xs: "1rem", md: "1.125rem" },
                  fontWeight: 500,
                  color: "#334155",
                  textAlign: "left",
                }}
              >
                {option.label}
              </Typography>
            </Button>
          );
        })}
      </Box>
    </Box>
  );
};

export default InjuryStep;
